import { useCallback, useEffect, useRef, useState } from "react";
import { graphqlRequest } from "../common/apiClient";

const POLL_INTERVAL_MS = Number.parseInt(
  process.env.REACT_APP_DASHBOARD_POLL_MS || "30000",
  10
);

const DASHBOARD_FIELDS_BASE = `
  id
  avg_duration
  avg_size
  errors
  pending
  success_rate
  total_documents
  total_size
  updated_at
  next_poll_seconds
`;

const DASHBOARD_FIELDS_V2 = `
  invoice_subtype_stats {
    subtype
    label
    count
    percentage
  }
  email_stats {
    processed_count
    avg_daily
    avg_weekly
    avg_monthly
    top_senders {
      sender
      display_name
      count
    }
    subject_word_cloud {
      text
      weight
    }
    recent_files {
      document_id
      filename
      status
      doc_type
      end_at
      email_id
      email_subject
      email_from
    }
  }
  recent_errors {
    document_id
    filename
    status
    doc_type
    end_at
  }
  recent_unclassified {
    document_id
    filename
    status
    doc_type
    end_at
  }
  recommended_actions {
    id
    priority
    severity
    title
    description
    action_type
    deeplink
    count
  }
`;

const DASHBOARD_FIELDS_V3 = `
  daily_stats {
    processed_count
    processed_size_mb
    date
  }
  origin_stats {
    origin
    label
    count
    percentage
  }
  top_suppliers {
    rank
    cuit
    name
    document_count
  }
  supplier_type_stats {
    type
    supplier_count
    document_count
  }
  currency_stats {
    ars_percentage
    other_percentage
    breakdown {
      currency
      count
      percentage
      total_amount
    }
  }
  financial_stats {
    avg_invoice_amount
    avg_invoice_currency
    avg_line_item_amount
    avg_credit_note_amount
    avg_debit_note_amount
    invoice_sample_count
    credit_note_sample_count
    debit_note_sample_count
    line_item_sample_count
    fiscal_document_count
  }
  extraction_fallback_stats {
    triggered_count
    documents_with_fallback
    multimodal_count
    textract_count
    total_input_tokens
    total_output_tokens
    total_cost_usd
  }
`;

const DASHBOARD_FIELDS_DAILY = `
  daily_invoice_subtype_stats {
    subtype
    label
    count
    percentage
  }
  daily_origin_stats {
    origin
    label
    count
    percentage
  }
  daily_currency_stats {
    ars_percentage
    other_percentage
    breakdown {
      currency
      count
      percentage
      total_amount
    }
  }
  daily_financial_stats {
    avg_invoice_amount
    avg_invoice_currency
    avg_line_item_amount
    avg_credit_note_amount
    avg_debit_note_amount
    invoice_sample_count
    credit_note_sample_count
    debit_note_sample_count
    line_item_sample_count
    fiscal_document_count
  }
  daily_extraction_fallback_stats {
    triggered_count
    documents_with_fallback
    multimodal_count
    textract_count
    total_input_tokens
    total_output_tokens
    total_cost_usd
  }
`;

const buildDashboardQuery = (fields) => `
  query GetDashboardStats {
    getDashboardStats {
      ${fields}
    }
  }
`;

export const DASHBOARD_QUERY = buildDashboardQuery(
  `${DASHBOARD_FIELDS_BASE}\n${DASHBOARD_FIELDS_V2}\n${DASHBOARD_FIELDS_V3}\n${DASHBOARD_FIELDS_DAILY}`
);

const DASHBOARD_QUERY_V3 = buildDashboardQuery(
  `${DASHBOARD_FIELDS_BASE}\n${DASHBOARD_FIELDS_V2}\n${DASHBOARD_FIELDS_V3}`
);

const DASHBOARD_QUERY_V2 = buildDashboardQuery(
  `${DASHBOARD_FIELDS_BASE}\n${DASHBOARD_FIELDS_V2}`
);

const DASHBOARD_QUERY_LEGACY = `
  query GetDashboardStatsLegacy {
    getDashboardStats(id: "stats") {
      id
      avg_duration
      avg_size
      errors
      pending
      success_rate
      total_documents
      total_size
      format_stats {
        id
        qnt
        mymetype
        total_size
      }
      doc_type_stats {
        id
        qnt
        doc_type
        total_size
      }
    }
  }
`;

const DASHBOARD_QUERY_DAILY = buildDashboardQuery(DASHBOARD_FIELDS_DAILY);

const EMPTY_EMAIL_STATS = {
  processed_count: 0,
  top_senders: [],
  subject_word_cloud: [],
  avg_daily: 0,
  avg_weekly: 0,
  avg_monthly: 0,
  recent_files: [],
};

const EMPTY_FINANCIAL_STATS = {
  avg_invoice_amount: null,
  avg_invoice_currency: 'ARS',
  avg_line_item_amount: null,
  avg_credit_note_amount: null,
  avg_debit_note_amount: null,
  invoice_sample_count: 0,
  credit_note_sample_count: 0,
  debit_note_sample_count: 0,
  line_item_sample_count: 0,
  fiscal_document_count: 0,
};

function withLegacyDefaults(stats) {
  if (!stats) return null;
  return {
    ...stats,
    invoice_subtype_stats: stats.invoice_subtype_stats || [],
    daily_invoice_subtype_stats: stats.daily_invoice_subtype_stats || [],
    email_stats: stats.email_stats || EMPTY_EMAIL_STATS,
    daily_stats: stats.daily_stats || {
      processed_count: 0,
      processed_size_mb: 0,
      date: new Date().toISOString().slice(0, 10),
    },
    origin_stats: stats.origin_stats || [],
    daily_origin_stats: stats.daily_origin_stats || [],
    top_suppliers: stats.top_suppliers || [],
    supplier_type_stats: stats.supplier_type_stats || [],
    currency_stats: stats.currency_stats || null,
    daily_currency_stats: stats.daily_currency_stats || null,
    financial_stats: stats.financial_stats || EMPTY_FINANCIAL_STATS,
    daily_financial_stats: stats.daily_financial_stats || EMPTY_FINANCIAL_STATS,
    recent_errors: stats.recent_errors || [],
    recent_unclassified: stats.recent_unclassified || [],
    recommended_actions: stats.recommended_actions || [],
    updated_at: stats.updated_at || new Date().toISOString(),
    next_poll_seconds: stats.next_poll_seconds || 30,
  };
}

let dailyStatsSupported = null;

async function fetchDailyStats() {
  if (dailyStatsSupported === false) {
    return {};
  }

  try {
    const data = await graphqlRequest(DASHBOARD_QUERY_DAILY);
    dailyStatsSupported = true;
    return data?.getDashboardStats || {};
  } catch (error) {
    dailyStatsSupported = false;
    console.warn("Dashboard daily stats unavailable (API schema without daily_* fields):", error);
    return {};
  }
}

async function fetchCoreStats() {
  const tiers = [
    { name: "command-center", query: DASHBOARD_QUERY },
    { name: "v3", query: DASHBOARD_QUERY_V3 },
    { name: "v2", query: DASHBOARD_QUERY_V2 },
    { name: "legacy", query: DASHBOARD_QUERY_LEGACY },
  ];

  let lastError = null;

  for (const tier of tiers) {
    try {
      const data = await graphqlRequest(tier.query);
      if (tier.name !== "v3") {
        console.warn(`Dashboard loaded with ${tier.name} API schema fallback`);
      }
      return data?.getDashboardStats || {};
    } catch (error) {
      lastError = error;
      console.warn(`Dashboard ${tier.name} query failed:`, error);
    }
  }

  throw lastError || new Error("No se pudieron cargar las estadísticas del dashboard");
}

function coreStatsIncludeDailyFields(stats = {}) {
  return Boolean(
    stats.daily_stats
    || stats.daily_invoice_subtype_stats
    || stats.daily_origin_stats
    || stats.daily_currency_stats
    || stats.daily_financial_stats
  );
}

export function useDashboardStats() {
  const [stats, setStats] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const intervalRef = useRef(null);
  const inFlightRef = useRef(false);

  const fetchStats = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      setFetchError(null);
      const coreStats = await fetchCoreStats();
      const dailyStats = coreStatsIncludeDailyFields(coreStats) ? {} : await fetchDailyStats();
      setStats(withLegacyDefaults({ ...coreStats, ...dailyStats }));
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error fetching dashboard stats:", err);
      setFetchError(err.message || "Error al cargar el dashboard");
    } finally {
      inFlightRef.current = false;
      setInitialLoad(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();

    intervalRef.current = setInterval(fetchStats, POLL_INTERVAL_MS);
    const onFocus = () => fetchStats();
    window.addEventListener("focus", onFocus);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchStats]);

  return {
    stats,
    lastUpdated,
    refresh: fetchStats,
    loading: initialLoad,
    error: fetchError,
  };
}
